import React, { useState, useEffect, useRef } from "react";
import { useTheme } from '../../context/ThemeContext';
import { motion } from "framer-motion";
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHashtag } from "@fortawesome/free-solid-svg-icons";






const TextToSpeech = () => {
    const [text, setText] = useState("");
  const { darkMode } = useTheme();
    const [voices, setVoices] = useState([]);
    const [selectedVoice, setSelectedVoice] = useState("");
    const [rate, setRate] = useState(1);
    const [pitch, setPitch] = useState(1);
    const [volume, setVolume] = useState(1);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const synthRef = useRef(null);

    useEffect(() => {
        // Initialize speech synthesis
        synthRef.current = window.speechSynthesis;

        const populateVoices = () => {
            const availableVoices = synthRef.current.getVoices();
            setVoices(availableVoices);
            if (availableVoices.length > 0 && !selectedVoice) {
                setSelectedVoice(
                    availableVoices.find((voice) => voice.default)?.name ||
                    availableVoices[0].name
                );
            }
        };

        populateVoices();
        
        // Some browsers require this event listener
        if (speechSynthesis.onvoiceschanged !== undefined) {
            speechSynthesis.onvoiceschanged = populateVoices;
        }

        return () => {
            // Clean up any ongoing speech when component unmounts
            if (synthRef.current.speaking) {
                synthRef.current.cancel();
            }
        };
    }, [selectedVoice]);

    const speak = () => {
        if (!text.trim()) {
            alert("Please enter some text to speak");
            return;
        }

        if (synthRef.current.speaking) {
            synthRef.current.cancel();
        }

        const utterance = new SpeechSynthesisUtterance(text);
        const voice = voices.find((v) => v.name === selectedVoice);

        if (voice) {
            utterance.voice = voice;
        }

        utterance.rate = rate;
        utterance.pitch = pitch;
        utterance.volume = volume;

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = (event) => {
            console.error("SpeechSynthesis error:", event);
            setIsSpeaking(false);
        };

        synthRef.current.speak(utterance);
    };

    const stopSpeaking = () => {
        synthRef.current.cancel();
        setIsSpeaking(false);
    };

    const handleKeyDown = (e) => {
        if (e.ctrlKey && e.key === "Enter") {
            e.preventDefault();
            if (isSpeaking) {
                stopSpeaking();
            } else {
                speak();
            }
        }
    };

    // Simple speaker icon component since we're not using Font Awesome
    const SpeakerIcon = ({ speaking }) => (
        <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className={speaking ? "text-red-500" : "text-green-500"}
        >
            {speaking ? (
                <>
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <line x1="23" y1="9" x2="17" y2="15"></line>
                    <line x1="17" y1="9" x2="23" y2="15"></line>
                </>
            ) : (
                <>
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                </>
            )}
        </svg>
    );

    return (
        <>
    <HeaderSection toolId="textToSpeech" />
   
        <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
            <div className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                
  <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faHashtag}
                                className="mr-3"
                            />
                            Text to Speech Converter
                        </h1>
                        <p className="opacity-90">
                            Convert text to speech with comprehensive
                            validation checks
                        </p>
                    </div>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className={`w-full mx-auto bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800   dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 p-6 border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 ${
                    isSpeaking ? "ring-2 ring-green-500 ring-opacity-50" : ""
                }`}
            >
                <header className="flex items-center gap-3 mb-6">
                    <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        className="bg-green-100 w-12 h-12 rounded-xl flex items-center justify-center text-green-600"
                    >
                        <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="24" 
                            height="24" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        >
                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                            <line x1="12" y1="19" x2="12" y2="23"></line>
                            <line x1="8" y1="23" x2="16" y2="23"></line>
                        </svg>
                    </motion.div>
                    <h2 className="text-2xl font-semibold text-gray-800">
                        Text to Speech Converter
                    </h2>
                </header>

                <div className="space-y-5">
                    <div>
                        <label
                            htmlFor="text"
                            className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-2"
                        >
                            Enter your text
                        </label>
                        <textarea
                            id="text"
                            rows={4}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Type something to hear it spoken aloud..."
                            className="w-full px-4 py-3 bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border border-gray-300 rounded-lg text-gray-800 dark:text-white dark:text-white dark:text-white dark:text-gray-100 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mt-1">
                            Press Ctrl+Enter to {isSpeaking ? "stop" : "speak"}
                        </p>
                    </div>

                    <div>
                        <label
                            htmlFor="voiceSelect"
                            className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-2"
                        >
                            Voice
                            <span
                                className="ml-1 text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 hover:text-gray-600 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-300 dark:text-gray-300 cursor-help"
                                title="Voice options may vary by browser and system"
                            >
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="16" 
                                    height="16" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12" y2="8"></line>
                                </svg>
                            </span>
                        </label>
                        <select
                            id="voiceSelect"
                            value={selectedVoice}
                            onChange={(e) => setSelectedVoice(e.target.value)}
                            className="w-full px-4 py-3 bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 border border-gray-300 rounded-lg text-gray-800 dark:text-white dark:text-white dark:text-white dark:text-gray-100 dark:text-gray-100 appearance-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                            {voices.length === 0 && (
                                <option value="" disabled>
                                    Loading voices...
                                </option>
                            )}
                            {voices.map((voice, index) => (
                                <option key={index} value={voice.name}>
                                    {voice.name} ({voice.lang})
                                    {voice.default ? " — DEFAULT" : ""}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-2">
                                Speech Rate:{" "}
                                <span className="text-green-600">{rate.toFixed(1)}</span>
                            </label>
                            <div className="flex items-center gap-3">
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="text-gray-500"
                                >
                                    <path d="M10 20v-4h4"></path>
                                    <path d="M10 12h4"></path>
                                    <path d="M10 4v4h4"></path>
                                </svg>
                                <input
                                    type="range"
                                    min="0.5"
                                    max="2"
                                    step="0.1"
                                    value={rate}
                                    onChange={(e) =>
                                        setRate(parseFloat(e.target.value))
                                    }
                                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-green-500 hover:[&::-webkit-slider-thumb]:bg-green-600"
                                />
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="text-gray-500"
                                >
                                    <path d="M10 20v-4h4"></path>
                                    <path d="M10 12h4"></path>
                                    <path d="M10 4v4h4"></path>
                                    <path d="M18 8a4 4 0 0 1-4 4"></path>
                                    <path d="M6 8a4 4 0 0 0 4 4"></path>
                                </svg>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-2">
                                Pitch:{" "}
                                <span className="text-green-600">{pitch.toFixed(1)}</span>
                            </label>
                            <div className="flex items-center gap-3">
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="text-gray-500"
                                >
                                    <path d="M12 5v14"></path>
                                    <path d="M5 12h14"></path>
                                </svg>
                                <input
                                    type="range"
                                    min="0"
                                    max="2"
                                    step="0.1"
                                    value={pitch}
                                    onChange={(e) =>
                                        setPitch(parseFloat(e.target.value))
                                    }
                                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-green-500 hover:[&::-webkit-slider-thumb]:bg-green-600"
                                />
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="text-gray-500"
                                >
                                    <path d="M12 5v14"></path>
                                    <path d="M5 12h14"></path>
                                    <path d="M18 8a4 4 0 0 1-4 4"></path>
                                    <path d="M6 8a4 4 0 0 0 4 4"></path>
                                </svg>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-2">
                                Volume:{" "}
                                <span className="text-green-600">{volume.toFixed(1)}</span>
                            </label>
                            <div className="flex items-center gap-3">
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="text-gray-500"
                                >
                                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                                    <line x1="15" y1="12" x2="15" y2="12"></line>
                                </svg>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={volume}
                                    onChange={(e) =>
                                        setVolume(parseFloat(e.target.value))
                                    }
                                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-green-500 hover:[&::-webkit-slider-thumb]:bg-green-600"
                                />
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="text-gray-500"
                                >
                                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                                </svg>
                            </div>
                        </div>
                    </div>

                    <motion.button
                        onClick={isSpeaking ? stopSpeaking : speak}
                        whileHover={{ y: -2 }}
                        whileTap={{ y: 0 }}
                        className={`w-full py-3.5 px-6 rounded-lg font-medium flex items-center justify-center gap-2 mt-6 ${
                            isSpeaking
                                ? "bg-red-600 hover:bg-red-700 text-white"
                                : "bg-green-600 hover:bg-green-700 text-white"
                        } transition-all`}
                    >
                        <SpeakerIcon speaking={isSpeaking} />
                        {isSpeaking ? "Stop Speaking" : "Speak Text"}
                    </motion.button>
                </div>
            </motion.div>
        </div>
        </div>
        <BottomSection toolId="textToSpeech" />
        </>
    );
};

export default TextToSpeech;